import { lazy, Suspense, useEffect } from 'react'

import { createFileRoute } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-adapter'

import { z } from 'zod'

import { CaseManagementSkeleton } from '@/features/air/case-management/components/skeletons'
import { caseQueries } from '@/features/air/case-management/queries'
import { useCaseListSearchStore } from '@/features/air/case-management/store'
import { RouteErrorComponent } from '@/features/app-shell/route-error-component'

import { getCurrentTenant } from '@/shared/auth/store'

import { ErrorBoundary } from '@/components/common/error-boundary'

const CaseManagementTable = lazy(
	() => import('@/features/air/case-management/components/table'),
)

const searchSchema = z.object({
	openedAfter: z.string().optional(),
	openedBefore: z.string().optional(),
	// Every key a filter group can write. A key missing here is **silently
	// stripped** from the URL by the validator, so the dropdown appears to do
	// nothing — add the group and its key together.
	filters: z
		.object({
			status: z.string().optional(),
			severity: z.string().optional(),
			source: z.string().optional(),
			/** The case's `alert_type` — the policy type that produced it. */
			type: z.string().optional(),
			investigation: z.string().optional(),
		})
		.optional(),
	q: z.string().optional(),
})

export const Route = createFileRoute('/_dashboard/air/_layout/case-management')(
	{
		validateSearch: zodValidator(searchSchema),

		loaderDeps: ({ search }) => ({
			openedAfter: search.openedAfter,
			openedBefore: search.openedBefore,
		}),

		loader: ({ context: { queryClient }, deps }) => {
			const tenant = getCurrentTenant()

			if (tenant) {
				queryClient.prefetchInfiniteQuery(
					caseQueries.cases_list(
						tenant,
						25,
						deps.openedAfter,
						deps.openedBefore,
					),
				)
			}
		},

		head: () => ({
			meta: [
				{
					title: 'Case Management | BitLyft AIR®',
				},
			],
		}),
		component: RouteComponent,
		// Same shape the list section falls back to — one definition, two moments.
		pendingComponent: CaseManagementSkeleton,
		errorComponent: RouteErrorComponent,
	},
)

function RouteComponent() {
	const search = Route.useSearch()
	const setLastListSearch = useCaseListSearchStore(
		(state) => state.setLastListSearch,
	)

	useEffect(() => {
		setLastListSearch(search)
	}, [search, setLastListSearch])

	return (
		<div className="flex h-full w-full flex-col items-start justify-start gap-6">
			<ErrorBoundary feature="case-management" section="list">
				<Suspense fallback={<CaseManagementSkeleton />}>
					<CaseManagementTable />
				</Suspense>
			</ErrorBoundary>
		</div>
	)
}
